import { useEffect, useState } from 'react'
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { intl } from '../../../../i18n'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { AggregateFieldToggleGroup } from '../../../components/toggleGroups/AggregateFieldToggleGroup'
import { WorkloadUnitToggleGroup } from '../../../components/toggleGroups/WorkloadUnitSelect'
import { Button } from '../../../components/buttons'

type Mode = 'initial' | 'change'

type Props = {
  mode: Mode
  isOpen: boolean
  defaultField: AggregateField
  defaultWorkloadUnit: WorkloadUnit
  onSubmit: (
    field: AggregateField,
    unit: WorkloadUnit,
    deleteExistingData: boolean
  ) => void
  onClose?: () => void
}

export const UnitSettingDialog = (props: Props) => {
  const { mode, isOpen, defaultField, defaultWorkloadUnit, onSubmit, onClose } =
    props

  const [field, setField] = useState<AggregateField>(defaultField)
  const [unit, setUnit] = useState<WorkloadUnit>(defaultWorkloadUnit)
  const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false)

  useEffect(() => {
    if (isOpen) {
      setField(defaultField)
      setUnit(defaultWorkloadUnit)
      setDeleteConfirmed(false)
    }
  }, [isOpen, defaultField, defaultWorkloadUnit])

  const fieldChanged = field !== defaultField

  return (
    <Dialog
      open={isOpen}
      fullWidth={true}
      maxWidth="xs"
      PaperProps={{ sx: { width: 460, maxWidth: '90vw' } }}
      disableEscapeKeyDown={mode === 'initial'}
      onClose={(_event, reason) => {
        if (mode === 'initial') return
        if (reason === 'backdropClick') return
        onClose?.()
      }}
      aria-labelledby="unit-setting-dialog-title"
    >
      <DialogTitle id="unit-setting-dialog-title" sx={{ fontSize: 16 }}>
        {intl.formatMessage({
          id:
            mode === 'initial'
              ? 'programProjectReport.unitSetting.initialTitle'
              : 'programProjectReport.unitSetting.changeTitle',
        })}
      </DialogTitle>
      <DialogContent dividers={true} sx={{ px: 3, py: 2.5 }}>
        {mode === 'initial' && (
          <Typography sx={{ mb: 2.5, color: 'text.secondary', fontSize: 13 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.description',
            })}
            <br />
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.descriptionUsage',
            })}
          </Typography>
        )}

        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ mb: 0.5, fontWeight: 600, fontSize: 14 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.aggregateFieldLabel',
            })}
          </Typography>
          <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: 12 }}>
            {intl.formatMessage({
              id: 'programProjectReport.unitSetting.aggregateFieldCaption',
            })}
          </Typography>
          <AggregateFieldToggleGroup
            value={field}
            onChange={value => setField(value)}
          />
        </Box>

        {mode === 'initial' && (
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ mb: 0.5, fontWeight: 600, fontSize: 14 }}>
              {intl.formatMessage({
                id: 'programProjectReport.unitSetting.workloadUnitLabel',
              })}
            </Typography>
            <Typography sx={{ mb: 1, color: 'text.secondary', fontSize: 12 }}>
              {intl.formatMessage({
                id: 'programProjectReport.unitSetting.workloadUnitCaption',
              })}
            </Typography>
            <WorkloadUnitToggleGroup value={unit} refresh={setUnit} />
          </Box>
        )}

        {mode === 'change' && fieldChanged && (
          <FormControlLabel
            sx={{ mt: 1, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={deleteConfirmed}
                onChange={e => setDeleteConfirmed(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ mt: 0.75 }}>
                {intl.formatMessage({
                  id: 'programProjectReport.unitSetting.deleteProgress',
                })}
              </Typography>
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          colorPattern="skyBlue"
          onClick={() => onSubmit(field, unit, deleteConfirmed)}
        >
          {intl.formatMessage({
            id:
              mode === 'initial'
                ? 'programProjectReport.unitSetting.submit'
                : 'programProjectReport.unitSetting.change',
          })}
        </Button>
        {mode === 'change' && (
          <Button colorPattern="monotone" variant="outlined" onClick={onClose}>
            {intl.formatMessage({ id: 'dialog.cancel' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
